"use client";

import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useStore } from "@/app/context/StoreContext";
import { useTemplates } from "@/hooks/useTemplates";
import TemplateCard from "./TemplateCard";

interface Props {
  open: boolean;
  onClose: () => void;
  /** Заголовок модалки. По умолчанию «Шаблоны». В контексте «Создать вид» —
   *  можно передать «Выберите шаблон». */
  title?: string;
}

const ALL_CATEGORY = "Все";
const MY_CATEGORY  = "⭐ Мои";
const LAST_CATEGORY_KEY = "frogstat_templates_last_category";

export default function TemplatesModal({ open, onClose, title = "Шаблоны" }: Props) {
  // Загружаем только когда модалка открыта — autoLoad=false + ручной reload.
  const { templates, categories: rawCategories, loading, error, reload } = useTemplates(false);
  const { user } = useStore();

  useEffect(() => {
    if (open) {
      reload();
    } else {
      // При закрытии модалки чистим локальный поиск — иначе при повторном
      // открытии юзер видит старый фильтр и удивляется «куда делись шаблоны».
      // Активная категория сохраняется в sessionStorage намеренно (ниже).
      setSearch("");
    }
  }, [open, reload]);

  // Запоминаем последнюю выбранную категорию в sessionStorage — между
  // открытиями модалки в рамках одной вкладки сохраняется. После закрытия
  // вкладки сбрасывается на «Все» (дефолт).
  const [activeCategory, setActiveCategory] = useState<string>(() => {
    if (typeof window === "undefined") return ALL_CATEGORY;
    try { return sessionStorage.getItem(LAST_CATEGORY_KEY) ?? ALL_CATEGORY; }
    catch { return ALL_CATEGORY; }
  });
  useEffect(() => {
    try { sessionStorage.setItem(LAST_CATEGORY_KEY, activeCategory); } catch {}
  }, [activeCategory]);
  const [search, setSearch] = useState("");

  // Категории: "Все" всегда первая, затем "⭐ Мои" если у юзера есть личные,
  // потом реальные категории из БД.
  //
  // Тонкость: пока user не подтянулся из StoreContext (первые 100-300мс после
  // открытия модалки), templates.some(…user.id) даст false и вкладка «⭐ Мои»
  // мигнёт — появится после загрузки user. Чтобы избежать мигания, используем
  // fallback: если user пока null, смотрим на наличие ЛЮБОГО шаблона с
  // owner_user_id (значит у кого-то есть личные — скорее всего у нас).
  // Когда user придёт — фильтр уточнится.
  const hasMyTemplates = useMemo(() => {
    if (!templates.length) return false;
    if (user) return templates.some(t => t.ownerUserId === user.id);
    // user ещё грузится — оптимистично показываем вкладку если есть ЛЮБЫЕ
    // личные. Фильтр в `filtered` всё равно отработает после user.
    return templates.some(t => t.ownerUserId != null);
  }, [templates, user]);
  const categories = useMemo(() => {
    const base = [ALL_CATEGORY];
    if (hasMyTemplates) base.push(MY_CATEGORY);
    return [...base, ...rawCategories];
  }, [hasMyTemplates, rawCategories]);

  // Если активная категория перестала существовать (например после удаления
  // последнего шаблона в ней), возвращаемся на «Все».
  useEffect(() => {
    if (activeCategory !== ALL_CATEGORY && !categories.includes(activeCategory)) {
      setActiveCategory(ALL_CATEGORY);
    }
  }, [categories, activeCategory]);

  // Фильтрация
  const filtered = useMemo(() => {
    let list = templates;
    if (activeCategory === MY_CATEGORY) {
      list = list.filter(t => !!user && t.ownerUserId === user.id);
    } else if (activeCategory !== ALL_CATEGORY) {
      list = list.filter(t => t.category === activeCategory);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(t =>
        t.name.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q)
      );
    }
    return list;
  }, [templates, activeCategory, search, user]);

  // ESC закрывает
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || typeof window === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/35 p-4 ui-modal-backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="templates-modal-title"
    >
      <div
        className="flex max-h-[88vh] w-full max-w-[1120px] flex-col overflow-hidden rounded-[22px] border border-[#ece8e0] bg-white shadow-[0_28px_80px_rgba(15,23,42,0.24)] ui-modal-panel"
        onClick={e => e.stopPropagation()}
      >
        {/* Шапка */}
        <div className="flex items-center justify-between border-b border-[#ece8e0] bg-[#fbfaf7] px-6 py-5">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#ece8e0] bg-white text-lg">
              📋
            </div>
            <div className="min-w-0">
              <h2 id="templates-modal-title" className="truncate text-lg font-semibold text-gray-900">{title}</h2>
              <div className="mt-0.5 text-xs text-gray-500">
                {loading ? "Загружаем шаблоны" : `${templates.length} шаблонов доступно`}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-xl text-xl leading-none text-gray-500 transition-colors hover:bg-white hover:text-gray-900"
            aria-label="Закрыть"
          >
            ×
          </button>
        </div>

        {/* Поиск + категории */}
        <div className="border-b border-[#ece8e0] bg-white px-6 py-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="relative min-w-0 flex-1">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">⌕</span>
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Найти шаблон по названию или описанию"
                className="h-10 w-full rounded-xl border border-[#ece8e0] bg-[#fbfaf7] pl-9 pr-3 text-sm text-gray-900 transition-colors placeholder:text-gray-400 focus:border-[#d7d0c3] focus:bg-white focus:outline-none"
              />
            </div>
            <div className="text-xs text-gray-500">
              Найдено: <span className="font-semibold text-gray-800">{filtered.length}</span>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-1.5 overflow-x-auto" style={{ scrollbarWidth: "none" }}>
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                  activeCategory === cat
                    ? "bg-gray-900 text-white"
                    : "bg-[#f7f5f0] text-gray-600 hover:bg-[#eee9df]"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Галерея */}
        <div className="flex-1 overflow-y-auto bg-[#f8fafc] p-6">
          {loading && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-[250px] rounded-2xl border border-[#ece8e0] bg-white p-4">
                  <div className="flex items-center gap-3">
                    <div className="ui-skeleton h-10 w-10 rounded-xl" />
                    <div className="min-w-0 flex-1">
                      <div className="ui-skeleton h-4 w-3/4 rounded" />
                      <div className="ui-skeleton mt-2 h-3 w-24 rounded" />
                    </div>
                  </div>
                  <div className="ui-skeleton mt-5 h-14 w-full rounded-lg" />
                  <div className="mt-4 grid grid-cols-4 gap-1.5">
                    {Array.from({ length: 8 }).map((__, cellIndex) => (
                      <div key={cellIndex} className="ui-skeleton h-7 rounded-md" />
                    ))}
                  </div>
                  <div className="mt-5 flex gap-2">
                    <div className="ui-skeleton h-9 flex-1 rounded-xl" />
                    <div className="ui-skeleton h-9 flex-1 rounded-xl" />
                  </div>
                </div>
              ))}
            </div>
          )}
          {error && (
            <div className="rounded-2xl border border-red-100 bg-red-50 px-5 py-6 text-center text-sm text-red-700">Ошибка: {error}</div>
          )}
          {!loading && !error && filtered.length === 0 && (
            <div className="ui-empty-state rounded-2xl px-5 py-12 text-center">
              <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-xl bg-[#f7f5f0] text-lg text-gray-500">📋</div>
              <div className="mt-3 text-sm font-medium text-gray-700">
                {templates.length === 0 ? "Шаблонов пока нет" : "Ничего не найдено"}
              </div>
              <div className="mx-auto mt-2 max-w-md text-xs leading-relaxed text-gray-500">
                {templates.length === 0
                  ? "Создать шаблон можно из готового вида через действие «Сохранить как шаблон»."
                  : "Попробуйте другую категорию или измените поиск."}
              </div>
            </div>
          )}
          {!loading && !error && filtered.length > 0 && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map(t => (
                <TemplateCard key={t.id} template={t} onApplied={onClose} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
